/* eslint-disable max-depth */
import { AppletDesignVersionDocument, UpdateArgoMediaDocument } from '@redviking/argonaut-core-ui/types/db';
import { AppletDesignVersionStatusEnum } from '@redviking/argonaut-util/types/db-enums/applet';
import { type ExtendedEntityParameters } from '@redviking/argonaut-core-ui/src/components/EntityDetail';
import { EntityDetailMode } from '@redviking/argonaut-core-ui/src/components/EntityDetail/mode';
import { Notify } from '@redviking/argonaut-core-ui/src/notifications';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import type { Route } from 'vue-router';
import { apiTrpcHttpClient } from '@redviking/argonaut-core-ui/src/util/api-trpc';
import { validateAppletDesignVersion } from './validations/design.validations';
import { ValidationResult } from 'types';
import { v4 as uuidv4 } from 'uuid';
import { type Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { accessor } from 'src/store';
import isEqual from 'lodash.isequal';
import { updateDesignWithComponents } from 'src/util/designMigrationUtil';
import { getAppletDesignPendingMedia } from './util/applet-design-util';
import { generalTabRoute } from './util/consts';

export type AppletChangeLogExtraData = {
    type: 'screen' | 'scripts' | 'macros';
}

export type AppletDesignVersionEntity = {
    id: string;
    versionNumber: number | null;
    status: AppletDesignVersionStatusEnum;
    notes: string;
    config: Latest.AppletDesignVersionConfig;
    design: {
        id: string;
        name: string;
    };
    previousDesignVersion: {
        id: string;
        designId: string;
    } | null;
    ignoreUnlinkedCells: boolean;
    ignoreFunctionalFlows: boolean;
    ignoreDefaultScreenLayouts: boolean;
    // The warning appears per varp, so we need to track which ones have been warned about
    ignoreUdpSocketProviders: string[];
};

export async function getAppletDesignVersion (to: Route, from?: Route, mode?: EntityDetailMode) {

    accessor.appletDesign.reset();

    if (to.query.mode === 'create') {
        const newEntity: AppletDesignVersionEntity = {
            config: {
                appletVariables: {},
                fontFamily: 'sans-serif',
                lockout: null,
                macros: [],
                activeOperator: null,
                schemaVersion: 4,
                screens: [],
                scripts: [],
                flows: {},
                userAppletEvents: {},
            },
            design: {
                id: to.params.appletDesignId,
                name: 'New Applet Design',
            },
            id: to.params.appletDesignVersionId,
            notes: '',
            status: AppletDesignVersionStatusEnum.Staged,
            versionNumber: null,
            previousDesignVersion: null,
            ignoreUnlinkedCells: false,
            ignoreFunctionalFlows: false,
            ignoreUdpSocketProviders: [],
            ignoreDefaultScreenLayouts: false,
        };
        return { entity: newEntity, originalEntity: null };
    }

    const { appletDesignVersion } = await gqlClient.request({
        document: AppletDesignVersionDocument,
        variables: {
            id: to.params.appletDesignVersionId,
        },
    });

    if (!appletDesignVersion?.config) {
        const err = new Error('Not found');
        Notify.error(err);
        throw err;
    }

    // this step is important for applying migrations
    const configParseResult = updateDesignWithComponents(appletDesignVersion.config);
    if ('migrationSteps' in configParseResult) {
        throw new Error('Applet design requires migration, but can only be applied from the applet design list page');
    }

    const entity: AppletDesignVersionEntity = {
        config: configParseResult.newConfig,
        design: {
            id: appletDesignVersion.design.id,
            name: appletDesignVersion.design.name,
        },
        id: appletDesignVersion.id,
        notes: appletDesignVersion.notes,
        status: appletDesignVersion.status,
        versionNumber: appletDesignVersion.versionNumber || null,
        previousDesignVersion: appletDesignVersion.previousDesignVersion || null,
        ignoreUnlinkedCells: false,
        ignoreFunctionalFlows: false,
        ignoreUdpSocketProviders: [],
        ignoreDefaultScreenLayouts: false,
    };

    if (mode === EntityDetailMode.Fork) {
        entity.versionNumber = null;
        entity.id = uuidv4();
        entity.status = AppletDesignVersionStatusEnum.Staged;
        entity.previousDesignVersion = {
            id: appletDesignVersion.id,
            designId: appletDesignVersion.design.id,
        };
    }

    if (mode === EntityDetailMode.Copy) {
        entity.versionNumber = null;
        entity.id = uuidv4();
        entity.status = AppletDesignVersionStatusEnum.Staged;
        entity.design = {
            ...entity.design,
            id: uuidv4(),
            name: `Copy of ${entity.design.name}`,
        };
        entity.notes += `\n\nCopied from ${appletDesignVersion.design.name} v${appletDesignVersion.versionNumber}`;
        entity.previousDesignVersion = {
            id: appletDesignVersion.id,
            designId: appletDesignVersion.design.id,
        };
    }

    return { entity };
}

export async function saveAppletDesignVersion (payload: ExtendedEntityParameters<'appletDesignVersion'>): Promise<ValidationResult> {
    const {
        mode,
        entity,
        oldEntity,
        featureFlags,
    } = payload;
    const validationResult = await validateAppletDesignVersion({
        entityData: entity,
        pendingMedia: getAppletDesignPendingMedia(),
        featureFlags,
    });
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    try {
        const designVersionId = entity.id === 'new' ? uuidv4() : entity.id;
        const designId = entity.design.id === 'new' ? uuidv4() : entity.design.id;

        if (mode === EntityDetailMode.Edit) {
            const updateResult = await apiTrpcHttpClient.applet.designVersion.update.mutate({
                appletDesignVersion: {
                    config: entity.config,
                    id: designVersionId,
                    notes: entity.notes,
                    design: {
                        name: entity.design.name,
                    },
                },
            });

            // get all the media and update the meta data
            for (const screen of updateResult.config.screens) {
                for (const mediaCell of screen.cells.filter(cell => cell.type === 'media')) {
                    for (const mediaItem of mediaCell.attrs.items) {
                        if (mediaItem.media.type === 'internal') {
                            const existingScreen = oldEntity?.config.screens.find(s => s.id === screen.id);
                            const existingMediaCell = existingScreen?.cells.find((c): c is Latest.Screen.Cells.Media.Cell => c.type === 'media' && c.id === mediaCell.id);
                            const existingLinkedMedia = existingMediaCell?.attrs.items.find(olm => olm.media.type === 'internal' && mediaItem.media.type === 'internal' && olm.media.file.id === mediaItem.media.file.id);
                            // If the media is internal and the meta data has changed, update it
                            if (!existingLinkedMedia || (existingLinkedMedia.media.type === 'internal' && !isEqual(existingLinkedMedia.media.file.meta, mediaItem.media.file.meta))) {
                                await gqlClient.request({
                                    document: UpdateArgoMediaDocument,
                                    variables: {
                                        mediaId: mediaItem.media.file.id,
                                        mediaInput: {
                                            meta: mediaItem.media.file.meta,
                                        },
                                    },
                                });
                            }
                        }
                    }
                }
            }
        } else {
            await apiTrpcHttpClient.applet.designVersion.insert.mutate({
                appletDesignVersion: {
                    config: entity.config,
                    id: designVersionId,
                    notes: entity.notes,
                    design: {
                        id: designId,
                        name: entity.design.name,
                    },
                    previousDesignVersionId: entity.previousDesignVersion?.id,
                },
            });

        }
        return {
            status: 'success',
            newRoute: {
                name: generalTabRoute,
                params: {
                    appletDesignId: designId,
                    appletDesignVersionId: designVersionId,
                },
            },
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Applet Design Version: "${JSON.stringify(err)}"`,
        };
    }
}
